import {
  AlignmentType,
  BorderStyle,
  Document,
  ExternalHyperlink,
  HeadingLevel,
  LevelFormat,
  Packer,
  Paragraph,
  TextRun,
  UnderlineType,
} from "docx";

const accent = "0F766E";
const textColor = "172033";
const mutedColor = "64748B";

export async function exportResumeDocx(resume, fileName = "resume.docx") {
  const doc = new Document({
    creator: "Resume Builder",
    description: "Resume generated by Resume Builder",
    title: resume.profile?.name ? `${resume.profile.name} Resume` : "Resume",
    styles: {
      default: {
        document: {
          run: {
            font: "Arial",
            size: 21,
            color: textColor,
          },
          paragraph: {
            spacing: { after: 80 },
          },
        },
      },
      paragraphStyles: [
        {
          id: "ResumeHeading",
          name: "Resume Heading",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            bold: true,
            color: accent,
            size: 20,
            allCaps: true,
          },
          paragraph: {
            spacing: { before: 220, after: 100 },
            border: {
              bottom: {
                color: "CCFBF1",
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
          },
        },
      ],
    },
    numbering: {
      config: [
        {
          reference: "resume-bullets",
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: "•",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 360, hanging: 180 },
                },
                run: {
                  font: "Arial",
                  size: 21,
                  color: textColor,
                },
              },
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720,
            },
          },
        },
      children: buildResumeChildren(resume),
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, fileName.endsWith(".docx") ? fileName : `${fileName}.docx`);
}

function buildResumeChildren(resume) {
  const sections = resume.sections || {};
  const children = [...buildHeader(resume)];

  if (sections.summary !== false && hasText(resume.summary)) {
    children.push(sectionHeading("Professional Summary"), paragraph(resume.summary));
  }

  const skills = (resume.skills || []).filter((group) => hasText(group.label) || (group.items || []).some(hasText));
  if (sections.skills !== false && skills.length) {
    children.push(sectionHeading("Core Skills"));
    skills.forEach((group) => {
      const items = (group.items || []).filter(hasText).join(", ");
      if (!hasText(items) && !hasText(group.label)) return;
      children.push(
        paragraph("", [
          run(`${hasText(group.label) ? group.label : "Skills"}: `, { bold: true }),
          run(items),
        ]),
      );
    });
  }

  const experience = (resume.experience || []).filter((item) => hasText(item.role) || hasText(item.company) || (item.bullets || []).some(hasText));
  if (sections.experience !== false && experience.length) {
    children.push(sectionHeading("Professional Experience"));
    experience.forEach((item) => {
      children.push(recordTitle([item.role, item.company].filter(hasText).join(" - "), [item.start, item.end].filter(hasText).join(" - ")));
      if (hasText(item.location)) children.push(mutedParagraph(item.location));
      children.push(...bulletParagraphs(item.bullets));
    });
  }

  const projects = (resume.projects || []).filter((item) => hasText(item.name) || hasText(item.role) || (item.bullets || []).some(hasText));
  if (sections.projects !== false && projects.length) {
    children.push(sectionHeading("Projects"));
    projects.forEach((item) => {
      children.push(recordTitle([item.name, item.role].filter(hasText).join(" - ")));
      if (hasText(item.url)) children.push(linkParagraph(item.url));
      children.push(...bulletParagraphs(item.bullets));
    });
  }

  const education = (resume.education || []).filter((item) => hasText(item.degree) || hasText(item.institute) || hasText(item.year));
  if (sections.education !== false && education.length) {
    children.push(sectionHeading("Education"));
    education.forEach((item) => {
      children.push(recordTitle(item.degree, item.year));
      children.push(mutedParagraph([item.institute, item.location].filter(hasText).join(", ")));
    });
  }

  const certifications = (resume.certifications || []).filter((item) => hasText(item.name) || hasText(item.issuer) || hasText(item.year));
  if (sections.certifications !== false && certifications.length) {
    children.push(sectionHeading("Certifications"));
    certifications.forEach((item) => {
      children.push(recordTitle(item.name, item.year));
      children.push(certificationMeta(item.issuer, item.url));
    });
  }

  return children;
}

function buildHeader(resume) {
  const profile = resume.profile || {};
  const contact = [
    contactPart(profile.email, isEmail(profile.email) ? `mailto:${profile.email}` : ""),
    contactPart(profile.phone),
    contactPart(profile.location),
    contactPart(profile.website, profile.website),
    contactPart(profile.linkedin, profile.linkedin),
    contactPart(profile.github, profile.github),
  ].filter(Boolean);

  return [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [run(profile.name || "Your Name", { bold: true, size: 34, color: "0F172A" })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 80 },
      children: [run(profile.title || "Target Role / Professional Headline", { bold: true, color: accent })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 180 },
      children: joinRuns(contact, " | "),
    }),
  ];
}

function sectionHeading(text) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    style: "ResumeHeading",
    children: [run(text.toUpperCase(), { bold: true, color: accent, size: 20 })],
  });
}

function recordTitle(left, right = "") {
  const children = [run(left || "", { bold: true, color: "0F172A" })];
  if (hasText(right)) children.push(rawRun(" | ", { color: mutedColor }), run(right, { color: mutedColor }));
  return new Paragraph({
    spacing: { before: 80, after: 30 },
    children,
  });
}

function mutedParagraph(text) {
  if (!hasText(text)) return paragraph("");
  return paragraph("", [run(text, { color: mutedColor, size: 19 })], { after: 45 });
}

function linkParagraph(url) {
  if (!hasText(url)) return paragraph("");
  return paragraph("", [hyperlink(url, url)], { after: 45 });
}

function certificationMeta(issuer, url) {
  const children = [];
  if (hasText(issuer)) children.push(run(issuer, { color: mutedColor, size: 19 }));
  if (hasText(url)) {
    if (children.length) children.push(rawRun(" | ", { color: mutedColor, size: 19 }));
    children.push(hyperlink(url, url));
  }
  return paragraph("", children, { after: 45 });
}

function paragraph(text, children = null, spacing = {}) {
  return new Paragraph({
    spacing: { after: spacing.after ?? 80, before: spacing.before ?? 0 },
    children: children || [run(text)],
  });
}

function bulletParagraphs(bullets = []) {
  return bullets.filter(hasText).map(
    (bullet) =>
      new Paragraph({
        children: [run(bullet)],
        numbering: { reference: "resume-bullets", level: 0 },
        spacing: { after: 55 },
      }),
  );
}

function run(text, options = {}) {
  return new TextRun({
    text: cleanText(text),
    bold: options.bold,
    color: options.color || textColor,
    size: options.size || 21,
    underline: options.underline ? { type: UnderlineType.SINGLE } : undefined,
  });
}

function rawRun(text, options = {}) {
  return new TextRun({
    text: String(text || ""),
    bold: options.bold,
    color: options.color || textColor,
    size: options.size || 21,
    underline: options.underline ? { type: UnderlineType.SINGLE } : undefined,
  });
}

function hyperlink(text, url) {
  return new ExternalHyperlink({
    link: normalizeUrl(url),
    children: [run(text, { color: "0E7490", size: 19, underline: true })],
  });
}

function contactPart(text, url = "") {
  if (!hasText(text)) return null;
  return hasText(url) ? hyperlink(text, url) : run(text, { color: mutedColor, size: 18 });
}

function joinRuns(items, separator) {
  return items.flatMap((item, index) => (index ? [rawRun(separator, { color: mutedColor, size: 18 }), item] : [item]));
}

function normalizeUrl(url) {
  const clean = cleanText(url);
  if (/^mailto:/i.test(clean) || /^https?:\/\//i.test(clean)) return clean;
  return `https://${clean}`;
}

function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanText(value));
}

function cleanText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function hasText(value) {
  return Boolean(cleanText(value));
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
